import React, { useEffect, useState } from "react";
import { Button, CustomModalProps, Icon, ModalTypes, Typography16, Typography28 } from "@slid/slid-ips";
import styled from "styled-components";
import { Trans, useTranslation } from "react-i18next";
import { waitImagesToLoad } from "../utils-ts";
import ChannelService from "config/channel-talk";
import { useAppSelector } from "hooks";

/**
 * This modal is shown when the user has passed the study challenge,
 * as a one-time notification.
 * @param showModal
 * @param closeModal
 */
export const showStudyChallengePassedModal = async (props: any) => {
  const { showModal, closeModal } = props;

  const modalProps: CustomModalProps = {
    type: ModalTypes.customModal,
    customComponent: <CommonComponents closeModal={closeModal} />,
  };
  showModal(modalProps);
};

const CommonComponents = ({ closeModal }: any) => {
  const { userData, lang } = useAppSelector((state) => state.slidGlobal);
  const { t } = useTranslation("StudyChallenge");

  // wait for the image to be loaded, in order to prevent text from
  // being separated with background image:
  const [imageLoaded, setImageLoaded] = useState<boolean>(false);
  const IMAGE_PATH = `/src/assets/study_challenge/success_cert_${lang === "ko" ? "ko" : "en"}.webp`;

  useEffect(() => {
    waitImagesToLoad([IMAGE_PATH]).then(() => {
      setImageLoaded(true);
    });
  }, []);

  return (
    <Container>
      {imageLoaded && (
        <ImageContainer>
          <RecipientContainer>
            <Typography16 weight={700} color="--gray1" style={{ color: "black" }} text={userData?.username} />
          </RecipientContainer>
          <Image src={IMAGE_PATH} />
        </ImageContainer>
      )}
      <Typography28
        weight={700}
        // @ts-ignore
        text={
          <Trans
            components={{
              colored: <Typography28 weight={700} text={t("PassedModal.TitleColoredText", { keySeparator: "." })} color={`--blue8`} textAlign={"center"} style={{ display: "inline" }} />,
            }}
          >
            {t("PassedModal.Title", { keySeparator: "." })}
          </Trans>
        }
        color="--gray17"
        marginTop="24px"
        textAlign="center"
      />
      <DescriptionArea>
        {t("PassedModal.Description", { keySeparator: ".", joinArrays: "||" })
          .split("||")
          .map((descriptionText, index) => (
            <DescriptionRow key={index}>
              <Icon icon="check24" color="--blue8" />
              <Typography16 weight={400} text={descriptionText} color="--gray15" />
            </DescriptionRow>
          ))}
      </DescriptionArea>
      <ButtonRow>
        {/* Close Button: */}
        <Button appearance="secondary" callback={closeModal} size="medium" text={t("PassedModal.SecondaryButtonText", { keySeparator: "." })} />
        {/* Open Channel Talk Button: */}
        <Button
          appearance="primary"
          callback={async () => {
            closeModal();
            setTimeout(() => {
              if (ChannelService) ChannelService.openChat();
            }, 0);
          }}
          size="medium"
          text={t("PassedModal.PrimaryButtonText", { keySeparator: "." })}
        />
      </ButtonRow>
    </Container>
  );
};

const Container = styled.div`
  padding-top: 39px;
  padding: 48px 36px;
  background-color: var(--gray1);
  border-radius: 20px;
  display: flex;
  align-items: center;
  flex-direction: column;
`;

const ImageContainer = styled.div`
  position: relative;
  width: 400px;
`;

const Image = styled.img`
  width: 400px;
`;

const RecipientContainer = styled.div`
  width: 400px;
  position: absolute;
  top: 118px;
  text-align: center;
`;

const DescriptionArea = styled.div`
  margin-top: 14px;
`;

const DescriptionRow = styled.div`
  display: flex;
  margin-top: 4px;
  gap: 8px;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 48px;
  justify-content: center;
`;
